import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Icon, type IconName } from '@/components/Icon';
import { useAsyncData } from '@/hooks/useAsyncData';
import { useSeenMarker } from '@/hooks/useSeenMarker';
import { fetchNotifications } from '@/api/dashboard';
import type { AttentionKind } from '@/api/dashboard';

const KIND_ICON: Record<AttentionKind, IconName> = {
  expired: 'alertCircle',
  expiring: 'clock',
  'no-tests': 'testChecklist',
};

/** Сколько пунктов помещаем в выпадашку; остальное — на дашборде. */
const VISIBLE = 3;

const SEEN_KEY = 'notifications:seen';

/**
 * Слепок списка для отметки «просмотрено»: клиент плюс причина. Именно причина,
 * а не только id — если у того же кабинета срок сменился с «истекает» на
 * «истёк», это новость, и значок должен загореться снова.
 */
function signature(items: { clientId: number; kind: string }[]): string {
  return items
    .map((item) => `${item.clientId}:${item.kind}`)
    .sort()
    .join('|');
}

/**
 * Колокольчик: те же клиенты, что в блоке «Требуют внимания» на дашборде —
 * истёкшие и истекающие кабинеты, кабинеты без подключённых тестов. Другого
 * источника уведомлений в админке нет: всё, что требует реакции менеджера,
 * это состояние его клиентов.
 *
 * Запрос лёгкий (только этот список) и уходит один раз при загрузке страницы —
 * ради значка с числом; полную сводку дашборда ради шапки не считаем.
 *
 * Красная точка — про НЕПРОСМОТРЕННОЕ, а не про «есть проблемы»: иначе она
 * висела бы постоянно, пока в портфеле есть хоть один просроченный кабинет, и
 * перестала бы что-либо значить. После открытия гаснет и загорается снова,
 * только когда в списке появляется что-то новое.
 */
export function NotificationsMenu() {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const { seen, markSeen } = useSeenMarker(SEEN_KEY);

  // Молча: не сумели получить — просто нет значка, шапка работает.
  const { data } = useAsyncData(fetchNotifications, []);
  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const hasUnread = items.length > 0 && signature(items) !== seen;

  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open]);

  return (
    <div className="topbar__menu-wrap" ref={wrapRef}>
      <button
        className="topbar__icon-btn"
        type="button"
        aria-label={total > 0 ? `Уведомления: ${total}` : 'Уведомления'}
        onClick={() => {
          if (!open && items.length > 0) markSeen(signature(items));
          setOpen((v) => !v);
        }}
      >
        <Icon name="bell" size={17} />
        {hasUnread && <span className="topbar__dot" />}
      </button>

      {open && (
        <div className="topbar__dropdown">
          <div className="topbar__dropdown-head">Требуют внимания</div>

          {items.length === 0 ? (
            <p className="topbar__dropdown-empty">Всё под контролем.</p>
          ) : (
            items.slice(0, VISIBLE).map((item) => (
              <Link
                key={item.clientId}
                className="topbar__dropdown-item"
                to={`/clients/${item.clientId}`}
                onClick={() => setOpen(false)}
              >
                <span className={`topbar__dropdown-icon topbar__dropdown-icon--${item.severity}`}>
                  <Icon name={KIND_ICON[item.kind]} size={15} />
                </span>
                <span className="topbar__dropdown-text">
                  <span className="topbar__dropdown-title">{item.company ?? 'Без названия'}</span>
                  <span className="topbar__dropdown-sub">{item.issue}</span>
                </span>
              </Link>
            ))
          )}

          {total > VISIBLE && (
            <div className="topbar__dropdown-foot">
              <Link to="/" onClick={() => setOpen(false)}>
                Ещё {total - VISIBLE} на дашборде
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
